<template>
  <BaseModalEditCreate
    mode="edit"
    :title="title"
    :is-modified="isModified"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div>
        <label for="customerName" class="text-sm font-medium">{{ $t('ranges.traderName') }}</label>
        <BaseInput
          id="customerName"
          v-model="modifiedData.customerName"
          :disabled="true"
          color="base" />
      </div>
      <div>
        <label for="traderSiteName" class="text-sm font-medium">{{
          $t('ranges.traderSiteName')
        }}</label>
        <BaseInput
          id="traderSiteName"
          v-model="modifiedData.traderSiteName"
          :disabled="true"
          color="base" />
      </div>
      <div>
        <label for="materialName" class="text-sm font-medium">{{
          $t('ranges.materialName')
        }}</label>
        <BaseInput
          id="materialName"
          v-model="modifiedData.materialName"
          :disabled="true"
          color="base" />
      </div>
      <div>
        <label for="materialAlterCodeValue" :required="false">{{
          $t('ranges.alterCodeCode')
        }}</label>
        <BaseInput
          id="materialAlterCodeValue"
          v-model="modifiedData.materialAlterCodeCode"
          :disabled="true"
          color="base" />
      </div>
      <div>
        <label for="lastDocumentDate" class="text-sm font-medium">{{
          $t('ranges.lastDocumentDate')
        }}</label>
        <BaseInput v-model="formattedLastDocumentDate" :disabled="true" color="base" />
      </div>
      <div>
        <label for="lastDocumentQuantity" class="text-sm font-medium">{{
          $t('ranges.quantity')
        }}</label>
        <BaseInput
          id="lastDocumentQuantity"
          v-model="modifiedData.lastDocumentQuantity"
          type="number"
          color="base"
          show-error-as-tooltip
          :error="
            $v.lastDocumentQuantity.required.$invalid
              ? $v.lastDocumentQuantity.required.$message
              : ''
          " />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import type { DynamicRange } from '@/types/Ranges';
import { deepCopy } from '@/utils/data';
import { helpers, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { useToastStore } from '@/stores/toast';
import { editDynamicRange } from '@/services/api/ranges';
import { isoDateToFullDate } from '@/utils/date';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = defineProps<{
  dynamicRange: DynamicRange;
  title?: string;
}>();

const modifiedData = ref<DynamicRange>(deepCopy<DynamicRange>(props.dynamicRange));
const isModified = ref<boolean>(false);

const validationModel = ref({
  lastDocumentQuantity: modifiedData.value.lastDocumentQuantity || '',
});

const rules = computed(() => ({
  lastDocumentQuantity: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

// Initialize a ref for the formatted date since it never changes
const formattedLastDocumentDate = ref(isoDateToFullDate(modifiedData.value.lastDocumentDate));

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.lastDocumentQuantity = newData.lastDocumentQuantity || '';
    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.dynamicRange);
  },
  { deep: true }
);

function onDismissChanges() {
  modifiedData.value = deepCopy<DynamicRange>(props.dynamicRange);
  emit('closeModal');
}
const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await editDynamicRange(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

onMounted(() => {
  if (modifiedData.value) {
    $v.value.$reset();
  }
});
</script>
